import { useSimpleSocketOptional } from "@/features/common/socket/SocketProvider";
import { accountType } from "@/shared/api/browser/accountType";
import { useSymbolIcon } from "@/shared/lib/hooks/useSymbolIcon";
import { tradingClient } from "@/shared/rpc/clients";
import { pairsStore } from "@/shared/stores/PairsStore";
import { sessionCounterStore, type SessionCounterViewMode } from "@/shared/stores/SessionCounterStore";
import { userStore } from "@/shared/stores/UserStore";
import { cn } from "@cronika/ui";
import { Icon } from "@iconify/react";
import { useTranslation } from "@cronika/locales";
import { AccountType } from "@cronika/proto/common/billing_pb";
import { SortDirection } from "@cronika/proto/common/common_pb";
import { BetLifecycle, BetOutcome, type BetMessage } from "@cronika/proto/common/trading_pb";
import { observer } from "mobx-react-lite";
import {
  FC,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

type SessionCounterWindowProps = {
  className?: string;
  obsMode?: boolean;
};

const REAL_ACCOUNT_TYPES = [AccountType.REAL, AccountType.BONUS, AccountType.PARTNER];
const MIN_WIDTH = 330;
const DEFAULT_HEIGHT = 265;
const MANUAL_HEIGHT = 284;
const FOOTER_MENU_HEIGHT = 43;
const EXPANDED_HEIGHT = DEFAULT_HEIGHT + FOOTER_MENU_HEIGHT;
const ROW_COLLAPSED_HEIGHT = 92.5;
const ROW_AUTO_EXPANDED_HEIGHT = 150.5;
const ROW_MANUAL_EXPANDED_HEIGHT = 251.5;
const CONTEXT_MENU_MIN_WIDTH = 140;
const CONTEXT_MENU_MAX_WIDTH = 320;
const CONTEXT_MENU_MIN_HEIGHT = 40;
const CONTEXT_MENU_OFFSET = 6;
const SEGMENT_TEXT_WIDTH_RATIO = 7.8;
const SEGMENT_BUTTON_EXTRA_WIDTH = 32;
const FOOTER_SIDE_BUTTON_WIDTH = 45;
const FOOTER_CONTROL_GAP = 7;
const FOOTER_CONTROL_PADDING = 6;
const WINDOW_HORIZONTAL_PADDING = 20;

const estimateSegmentButtonWidth = (label: string) => {
  return Math.ceil(label.length * SEGMENT_TEXT_WIDTH_RATIO + SEGMENT_BUTTON_EXTRA_WIDTH);
};

const formatElapsed = (seconds: number) => {
  const minutes = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const rest = (seconds % 60).toString().padStart(2, "0");
  return `${minutes}:${rest}`;
};

const formatMoney = (value: number) => {
  const sign = value > 0 ? "+" : value < 0 ? "-" : "";
  return `${sign}$${Math.abs(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const getBetPnl = (bet: BetMessage) => {
  if (bet.profitLoss != null && bet.profitLoss !== "") return Number(bet.profitLoss);
  if (bet.outcome === BetOutcome.WON) return Number(bet.amount || 0) * (Number(bet.payoutPercent || 0) / 100);
  if (bet.outcome === BetOutcome.LOST) return -Number(bet.amount || 0);
  return 0;
};

const matchesCurrentAccount = (bet: BetMessage) => {
  const current = accountType.get();
  if (current === "demo") return bet.accountType === AccountType.DEMO;
  return REAL_ACCOUNT_TYPES.includes(bet.accountType);
};

const accountRequestFilter = () => {
  const current = accountType.get();
  return current === "demo" ? { accountType: AccountType.DEMO } : { accountTypes: REAL_ACCOUNT_TYPES };
};

const StatValue: FC<{
  value: string | number;
  label: string;
  tone?: "green" | "pink" | "white";
}> = ({ value, label, tone = "white" }) => (
  <div className="flex h-full flex-1 flex-col items-center justify-center gap-[3px]">
    <div
      className={cn(
        "text-[21px] font-bold leading-[21px]",
        tone === "green" && "text-[#17C964]",
        tone === "pink" && "text-[#F31260]",
        tone === "white" && "text-[#F2F2F2]",
      )}
    >
      {value}
    </div>
    <div className="text-xs font-normal leading-[14px] text-[#8C8C8C]">{label}</div>
  </div>
);

const CounterButtons: FC<{
  onIncrement: () => void;
  onDecrement: () => void;
  decrementDisabled: boolean;
}> = ({ onIncrement, onDecrement, decrementDisabled }) => (
  <div className="flex items-center gap-[3px]">
    <button
      type="button"
      className="flex h-6 w-10 items-center justify-center rounded-md bg-[rgba(140,140,140,0.15)] text-[#FBFBFB]"
      onClick={onIncrement}
    >
      <Icon icon="solar:add-linear" width={14} />
    </button>
    <button
      type="button"
      className={cn(
        "flex h-6 w-10 items-center justify-center rounded-md bg-[rgba(140,140,140,0.15)] text-[#FBFBFB]",
        decrementDisabled && "cursor-not-allowed text-[#8C8C8C] opacity-35",
      )}
      onClick={onDecrement}
      disabled={decrementDisabled}
    >
      <Icon icon="solar:minus-linear" width={14} />
    </button>
  </div>
);

const ManualStatValue: FC<{
  value: string | number;
  label: string;
  tone?: "green" | "pink" | "white";
  onIncrement: () => void;
  onDecrement: () => void;
  decrementDisabled: boolean;
}> = ({ value, label, tone = "white", onIncrement, onDecrement, decrementDisabled }) => (
  <div className="flex h-[78px] flex-1 flex-col items-center justify-center gap-[3px] py-1.5">
    <div
      className={cn(
        "flex h-[21px] items-center text-[21px] font-bold leading-[21px]",
        tone === "green" && "text-[#17C964]",
        tone === "pink" && "text-[#F31260]",
        tone === "white" && "text-[#F2F2F2]",
      )}
    >
      {value}
    </div>
    <div className="flex h-[15px] items-center text-xs font-normal leading-[14px] text-[#8C8C8C]">{label}</div>
    <CounterButtons
      onIncrement={onIncrement}
      onDecrement={onDecrement}
      decrementDisabled={decrementDisabled}
    />
  </div>
);

const WinRateIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <g clipPath="url(#session-counter-winrate-icon)">
      <path
        d="M9.02175 8.21094C8.57364 8.21094 8.20898 8.57548 8.20898 9.02359C8.20898 9.47181 8.57364 9.83647 9.02175 9.83647C9.46997 9.83647 9.83441 9.47181 9.83441 9.02359C9.8343 8.57548 9.46986 8.21094 9.02175 8.21094Z"
        fill="currentColor"
      />
      <path
        d="M4.97108 4.16016C4.52286 4.16016 4.1582 4.52481 4.1582 4.97303C4.15831 5.42103 4.52297 5.78558 4.97108 5.78558C5.41919 5.78558 5.78363 5.42103 5.78363 4.97303C5.78363 4.52481 5.41919 4.16016 4.97108 4.16016Z"
        fill="currentColor"
      />
      <path
        d="M7 0C3.14005 0 0 3.14005 0 7C0 10.8597 3.14005 14 7 14C10.8595 14 13.9993 10.8597 13.9993 7C13.9993 3.14005 10.8595 0 7 0ZM4.96989 3.28377C5.90067 3.28377 6.6582 4.04108 6.6582 4.9723C6.6582 5.90308 5.90067 6.66061 4.96989 6.66061C4.03878 6.66061 3.28136 5.90308 3.28136 4.9723C3.28136 4.04108 4.03889 3.28377 4.96989 3.28377ZM4.45659 10.1623C4.37128 10.2478 4.25895 10.2907 4.14706 10.2907C4.03506 10.2907 3.92295 10.2478 3.83742 10.1623C3.66647 9.9913 3.66647 9.71392 3.83742 9.54319L9.54275 3.83764C9.71359 3.66669 9.99086 3.66669 10.1619 3.83764C10.3329 4.00859 10.3329 4.28597 10.1619 4.45681L4.45659 10.1623ZM9.02048 10.7109C8.08949 10.7109 7.33195 9.95345 7.33195 9.02234C7.33195 8.09156 8.08949 7.33403 9.02048 7.33403C9.95138 7.33403 10.7088 8.09156 10.7088 9.02234C10.7087 9.95345 9.95127 10.7109 9.02048 10.7109Z"
        fill="currentColor"
      />
    </g>
    <defs>
      <clipPath id="session-counter-winrate-icon">
        <rect width="14" height="14" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const ProfitIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <g clipPath="url(#session-counter-profit-icon)">
      <path
        d="M10.2812 4.94922C8.2457 4.94922 6.58984 6.60505 6.58984 8.64057C6.58984 10.6761 8.24567 12.3319 10.2812 12.3319C12.3167 12.3319 13.9999 10.6761 13.9999 8.64057C13.9999 6.60505 12.3167 4.94922 10.2812 4.94922ZM10.7022 10.6172C10.6986 10.6185 10.6949 10.6179 10.6913 10.6192V11.1015C10.6913 11.3282 10.5079 11.5116 10.2812 11.5116C10.0545 11.5116 9.87101 11.3282 9.87101 11.1015V10.6164C9.60088 10.5251 9.34205 10.3519 9.12078 10.0881C8.97498 9.91468 8.99741 9.65593 9.17126 9.51054C9.3447 9.36474 9.60425 9.38716 9.74884 9.56102C9.96592 9.81936 10.2115 9.9219 10.4214 9.84659C10.5828 9.78772 10.6913 9.63271 10.6913 9.46088C10.6913 9.23459 10.5075 9.05073 10.2812 9.05073C9.60266 9.05073 9.05073 8.49877 9.05073 7.82028C9.05073 7.32482 9.34552 6.8798 9.80175 6.68678C9.82434 6.67721 9.84826 6.67775 9.87104 6.66961V6.17969C9.87104 5.95298 10.0545 5.76953 10.2812 5.76953C10.5079 5.76953 10.6914 5.95298 10.6914 6.17969V6.67034C10.9037 6.74209 11.1108 6.85614 11.2958 7.03525C11.4584 7.19266 11.4624 7.45221 11.3046 7.61523C11.1471 7.77785 10.8872 7.78146 10.7246 7.62404C10.5251 7.43058 10.3044 7.36408 10.1218 7.4422C9.96956 7.50668 9.87101 7.65488 9.87101 7.82031C9.87101 8.04661 10.0549 8.23047 10.2812 8.23047C10.9597 8.23047 11.5116 8.78243 11.5116 9.46091C11.5117 9.97637 11.1864 10.441 10.7022 10.6172Z"
        fill="currentColor"
      />
      <path
        d="M0.410156 9.05078C0.183449 9.05078 0 9.23423 0 9.46094V13.5898C0 13.8165 0.183449 13.9999 0.410156 13.9999H1.6406V9.05078H0.410156Z"
        fill="currentColor"
      />
      <path
        d="M9.72759 2.56005L6.85652 0.0991348C6.7035 -0.0330449 6.47601 -0.0330449 6.32299 0.0991348L3.45195 2.56005C3.32138 2.67139 3.27452 2.85243 3.33418 3.01346C3.39346 3.17449 3.54686 3.28143 3.71872 3.28143H4.94916C4.94916 6.82734 4.94916 10.4539 4.94916 14C6.00577 14 6.73626 14 7.82022 14C8.04693 14 8.23038 13.8166 8.23038 13.5899V12.6543C6.77181 11.906 5.76947 10.3898 5.76947 8.64075C5.76947 6.89176 6.77184 5.37552 8.23038 4.62715V3.28146H9.46082C9.63265 3.28146 9.78608 3.17451 9.84536 3.01349C9.90505 2.85243 9.85818 2.67139 9.72759 2.56005Z"
        fill="currentColor"
      />
      <path
        d="M2.89844 5.76953C2.67173 5.76953 2.48828 5.95298 2.48828 6.17969V9.05075V13.9999C3.08738 13.9999 3.5472 13.9999 4.12888 13.9999V5.76953H2.89844Z"
        fill="currentColor"
      />
    </g>
    <defs>
      <clipPath id="session-counter-profit-icon">
        <rect width="14" height="14" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const ResetSessionIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M1.75 7C1.75 8.03835 2.05791 9.05339 2.63478 9.91674C3.21166 10.7801 4.0316 11.453 4.99091 11.8504C5.95022 12.2477 7.00582 12.3517 8.02422 12.1491C9.04262 11.9466 9.97809 11.4465 10.7123 10.7123C11.4465 9.97809 11.9466 9.04262 12.1491 8.02422C12.3517 7.00582 12.2477 5.95022 11.8504 4.99091C11.453 4.0316 10.7801 3.21166 9.91674 2.63478C9.05339 2.05791 8.03835 1.75 7 1.75C5.53231 1.75552 4.12357 2.32821 3.06833 3.34833L1.75 4.66667"
      stroke="currentColor"
      strokeWidth="1.08333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M1.75 1.75V4.66667H4.66667"
      stroke="currentColor"
      strokeWidth="1.08333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SettingsGearIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M6.99955 4.66797C7.61839 4.66797 8.21188 4.9138 8.64947 5.35139C9.08705 5.78897 9.33288 6.38246 9.33288 7.0013C9.33288 7.62014 9.08705 8.21363 8.64947 8.65122C8.21188 9.0888 7.61839 9.33464 6.99955 9.33464C6.38071 9.33464 5.78722 9.0888 5.34963 8.65122C4.91205 8.21363 4.66622 7.62014 4.66622 7.0013C4.66622 6.38246 4.91205 5.78897 5.34963 5.35139C5.78722 4.9138 6.38071 4.66797 6.99955 4.66797ZM6.99955 5.83464C6.69013 5.83464 6.39338 5.95755 6.17459 6.17634C5.9558 6.39514 5.83288 6.69188 5.83288 7.0013C5.83288 7.31072 5.9558 7.60747 6.17459 7.82626C6.39338 8.04505 6.69013 8.16797 6.99955 8.16797C7.30897 8.16797 7.60572 8.04505 7.82451 7.82626C8.0433 7.60747 8.16622 7.31072 8.16622 7.0013C8.16622 6.69188 8.0433 6.39514 7.82451 6.17634C7.60572 5.95755 7.30897 5.83464 6.99955 5.83464ZM5.83288 12.8346C5.68705 12.8346 5.56455 12.7296 5.54122 12.5896L5.32538 11.0438C4.95788 10.898 4.64288 10.6996 4.33955 10.4663L2.88705 11.0555C2.75872 11.1021 2.60122 11.0555 2.53122 10.9271L1.36455 8.9088C1.32885 8.8487 1.31626 8.77765 1.32915 8.70894C1.34203 8.64022 1.3795 8.57856 1.43455 8.53547L2.66538 7.56714L2.62455 7.0013L2.66538 6.41797L1.43455 5.46714C1.3795 5.42405 1.34203 5.36238 1.32915 5.29367C1.31626 5.22496 1.32885 5.15391 1.36455 5.0938L2.53122 3.07547C2.60122 2.94714 2.75872 2.89464 2.88705 2.94714L4.33955 3.53047C4.64288 3.30297 4.95788 3.10464 5.32538 2.9588L5.54122 1.41297C5.56455 1.27297 5.68705 1.16797 5.83288 1.16797H8.16622C8.31205 1.16797 8.43455 1.27297 8.45788 1.41297L8.67372 2.9588C9.04122 3.10464 9.35622 3.30297 9.65955 3.53047L11.1121 2.94714C11.2404 2.89464 11.3979 2.94714 11.4679 3.07547L12.6346 5.0938C12.7104 5.22214 12.6754 5.37964 12.5646 5.46714L11.3337 6.41797L11.3746 7.0013L11.3337 7.58464L12.5646 8.53547C12.6754 8.62297 12.7104 8.78047 12.6346 8.9088L11.4679 10.9271C11.3979 11.0555 11.2404 11.108 11.1121 11.0555L9.65955 10.4721C9.35622 10.6996 9.04122 10.898 8.67372 11.0438L8.45788 12.5896C8.43455 12.7296 8.31205 12.8346 8.16622 12.8346H5.83288ZM6.56205 2.33464L6.34622 3.85714C5.64622 4.00297 5.02788 4.3763 4.57872 4.89547L3.17288 4.2888L2.73538 5.04714L3.96622 5.9513C3.73288 6.63186 3.73288 7.37075 3.96622 8.0513L2.72955 8.9613L3.16705 9.71964L4.58455 9.11297C5.03372 9.6263 5.64622 9.99964 6.34038 10.1396L6.55622 11.668H7.44288L7.65872 10.1455C8.35288 9.99964 8.96538 9.6263 9.41455 9.11297L10.8321 9.71964L11.2696 8.9613L10.0329 8.05714C10.2662 7.37464 10.2662 6.6338 10.0329 5.9513L11.2637 5.04714L10.8262 4.2888L9.42038 4.89547C8.96201 4.36482 8.34024 4.00161 7.65288 3.86297L7.43705 2.33464H6.56205Z"
      fill="currentColor"
    />
  </svg>
);

export const SessionCounterWindow: FC<SessionCounterWindowProps> = observer(({ className, obsMode = false }) => {
  const { t } = useTranslation("layout");
  const activePair = pairsStore.activePair;
  const pairIcon = useSymbolIcon(activePair?.type, activePair?.baseAsset, activePair?.quoteAsset);
  const lastBet = useSimpleSocketOptional()?.lastBet ?? null;
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [bets, setBets] = useState<BetMessage[]>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [transparencySettingsOpen, setTransparencySettingsOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [pausedElapsedSeconds, setPausedElapsedSeconds] = useState<number | null>(null);
  const [pausedStartedAtMs, setPausedStartedAtMs] = useState<number | null>(null);
  const [totalPausedMs, setTotalPausedMs] = useState(0);
  const [position, setPosition] = useState({ x: 24, y: 24 });
  const [size, setSize] = useState({
    width: sessionCounterStore.windowWidth,
    height: sessionCounterStore.windowHeight,
  });
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const pausedBetIdsRef = useRef<Set<string>>(new Set());
  const currentAccountType = userStore.currentAccountType;
  const startedAt = sessionCounterStore.startedAt;
  const minimized = sessionCounterStore.minimized;
  const mode = sessionCounterStore.mode;
  const viewMode = sessionCounterStore.viewMode;
  const metricMode = sessionCounterStore.metricMode;
  const footerExpanded = sessionCounterStore.footerExpanded;
  const manualWinsDelta = sessionCounterStore.manualWinsDelta;
  const manualLossesDelta = sessionCounterStore.manualLossesDelta;
  const statsSnapshot = sessionCounterStore.statsSnapshot;
  const currentTimezone =
    (obsMode ? statsSnapshot?.timezone || sessionCounterStore.timezone : userStore.user?.timezone)?.trim() || "UTC";

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (obsMode) return;
    sessionCounterStore.setTimezone(currentTimezone);
  }, [currentTimezone, obsMode]);

  useEffect(() => {
    if (!startedAt) return;

    let cancelled = false;
    void tradingClient
      .listBets({
        ...accountRequestFilter(),
        createdAtFrom: startedAt,
        lifecycle: [BetLifecycle.ACCEPTED, BetLifecycle.SETTLED],
        pagination: { page: 1, size: 100 },
        sortBy: "createdAt",
        sortDir: SortDirection.DESC,
      })
      .then((response) => {
        if (!cancelled) setBets(response.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setBets([]);
      });

    return () => {
      cancelled = true;
    };
  }, [startedAt, currentAccountType]);

  useEffect(() => {
    if (!startedAt || !lastBet?.data || !matchesCurrentAccount(lastBet.data)) return;

    if (pausedElapsedSeconds !== null) {
      pausedBetIdsRef.current.add(lastBet.data.id);
      setBets((prev) => prev.filter((bet) => bet.id !== lastBet.data.id));
      return;
    }

    if (pausedBetIdsRef.current.has(lastBet.data.id)) return;

    const createdAt = lastBet.data.createdAt || lastBet.data.settledAt;
    if (createdAt && new Date(createdAt).getTime() < new Date(startedAt).getTime()) return;

    setBets((prev) => {
      if (lastBet.type === "bet.canceled" || lastBet.type === "bet.rejected") {
        return prev.filter((bet) => bet.id !== lastBet.data.id);
      }

      const nextBet = {
        ...lastBet.data,
        lifecycle:
          lastBet.type === "bet.settled"
            ? BetLifecycle.SETTLED
            : lastBet.type === "bet.accepted"
              ? BetLifecycle.ACCEPTED
              : lastBet.data.lifecycle,
      };

      const index = prev.findIndex((bet) => bet.id === nextBet.id);
      if (index === -1) return [nextBet, ...prev];

      const next = [...prev];
      next[index] = { ...next[index], ...nextBet };
      return next;
    });
  }, [lastBet, pausedElapsedSeconds, startedAt]);

  const settledBets = bets.filter((bet) => bet.lifecycle === BetLifecycle.SETTLED);
  const baseWins = settledBets.filter((bet) => bet.outcome === BetOutcome.WON).length;
  const baseLosses = settledBets.filter((bet) => bet.outcome === BetOutcome.LOST).length;
  const wins = Math.max(0, baseWins + manualWinsDelta);
  const losses = Math.max(0, baseLosses + manualLossesDelta);
  const total = wins + losses;
  const pnl = settledBets.reduce((sum, bet) => sum + getBetPnl(bet), 0);
  const winRate = total > 0 ? Math.round((wins / total) * 100) : 0;
  const runningElapsedSeconds = startedAt
    ? Math.max(0, Math.floor((nowMs - new Date(startedAt).getTime() - totalPausedMs) / 1000))
    : 0;
  const elapsedSeconds = pausedElapsedSeconds ?? runningElapsedSeconds;
  const currentTime = new Intl.DateTimeFormat("ru-RU", {
    timeZone: currentTimezone,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(new Date(nowMs));
  const pairSymbol = activePair?.symbol.replace(/\.OTC$/i, "") ?? "";
  const pairIsOtc = activePair?.isOtc ?? false;
  const normalizedPairIcon = pairIcon ?? null;
  const displayWins = obsMode && statsSnapshot ? statsSnapshot.wins : wins;
  const displayLosses = obsMode && statsSnapshot ? statsSnapshot.losses : losses;
  const displayTotal = obsMode && statsSnapshot ? statsSnapshot.total : total;
  const displayPnl = obsMode && statsSnapshot ? statsSnapshot.pnl : pnl;
  const displayWinRate = obsMode && statsSnapshot ? statsSnapshot.winRate : winRate;
  const displayElapsedSeconds = obsMode
    ? Math.max(0, Math.floor((nowMs - new Date(startedAt ?? nowMs).getTime()) / 1000))
    : elapsedSeconds;
  const displayPairSymbol = obsMode && statsSnapshot ? statsSnapshot.pairSymbol : pairSymbol;
  const displayPairIcon = obsMode && statsSnapshot ? statsSnapshot.pairIcon : normalizedPairIcon;
  const displayPairIsOtc = obsMode && statsSnapshot ? statsSnapshot.pairIsOtc : pairIsOtc;
  const metricValueClassName =
    metricMode === "profit"
      ? displayPnl >= 0
        ? "text-[#17C964]"
        : "text-[#F31260]"
      : displayWinRate > 0
        ? "text-[#17C964]"
        : "text-[#F2F2F2]";
  const modeSegmentMinWidth =
    Math.max(
      estimateSegmentButtonWidth(t("session-counter.auto-count")),
      estimateSegmentButtonWidth(t("session-counter.manual")),
    ) *
      2 +
    FOOTER_SIDE_BUTTON_WIDTH +
    FOOTER_CONTROL_GAP +
    FOOTER_CONTROL_PADDING +
    WINDOW_HORIZONTAL_PADDING;
  const metricSegmentMinWidth =
    Math.max(
      estimateSegmentButtonWidth(t("session-counter.profit")),
      estimateSegmentButtonWidth(t("session-counter.win-rate")),
    ) *
      2 +
    FOOTER_CONTROL_PADDING +
    WINDOW_HORIZONTAL_PADDING;
  const effectiveMinWidth = Math.max(MIN_WIDTH, modeSegmentMinWidth, metricSegmentMinWidth);

  const handlePointerMove = useCallback((event: PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;

    setPosition({
      x: Math.max(0, drag.originX + event.clientX - drag.startX),
      y: Math.max(0, drag.originY + event.clientY - drag.startY),
    });
  }, []);

  const handlePointerUp = useCallback(
    (event: PointerEvent) => {
      if (dragRef.current?.pointerId !== event.pointerId) return;
      dragRef.current = null;
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    },
    [handlePointerMove],
  );

  const startDrag = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if ((event.target as HTMLElement).closest("button")) return;

      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: position.x,
        originY: position.y,
      };
      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
    },
    [handlePointerMove, handlePointerUp, position.x, position.y],
  );

  useEffect(() => {
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [handlePointerMove, handlePointerUp]);

  const showBackground = !sessionCounterStore.transparentBackground;
  const surfaceBackground =
    "bg-[radial-gradient(100.98%_115.1%_at_50%_0%,rgba(255,184,0,0.055)_0%,rgba(255,184,0,0)_42%),#151515]";
  const transparentBackgroundOpacity = (100 - sessionCounterStore.transparency) / 100;
  const transparentSurfaceBackground = `radial-gradient(100.98% 115.1% at 50% 0%, rgba(255, 184, 0, ${0.055 * transparentBackgroundOpacity}) 0%, rgba(255, 184, 0, 0) 42%), rgba(21, 21, 21, ${transparentBackgroundOpacity})`;
  const isStarted = startedAt !== null;
  const isRowView = isStarted && viewMode === "row";
  const defaultHeight = isRowView
    ? footerExpanded
      ? mode === "manual"
        ? ROW_MANUAL_EXPANDED_HEIGHT
        : ROW_AUTO_EXPANDED_HEIGHT
      : ROW_COLLAPSED_HEIGHT
    : isStarted && footerExpanded
      ? mode === "manual"
        ? EXPANDED_HEIGHT + (MANUAL_HEIGHT - DEFAULT_HEIGHT)
        : EXPANDED_HEIGHT
      : !isStarted && !footerExpanded
        ? mode === "manual"
          ? MANUAL_HEIGHT - FOOTER_MENU_HEIGHT
          : DEFAULT_HEIGHT - FOOTER_MENU_HEIGHT
      : mode === "manual"
        ? MANUAL_HEIGHT
        : DEFAULT_HEIGHT;
  const minHeight = defaultHeight;
  const accountLabel =
    currentAccountType === "real" ? t("session-counter.account-real") : t("session-counter.account-demo");

  useEffect(() => {
    if (obsMode || minimized) return;

    setSize(() => {
      const nextSize = {
        width: effectiveMinWidth,
        height: defaultHeight,
      };
      sessionCounterStore.setWindowSize(nextSize.width, nextSize.height);
      return nextSize;
    });
  }, [defaultHeight, effectiveMinWidth, minimized, obsMode]);

  useEffect(() => {
    if (obsMode) return;

    sessionCounterStore.setStatsSnapshot({
      wins,
      losses,
      total,
      pnl,
      winRate,
      elapsedSeconds,
      timezone: currentTimezone,
      accountLabel,
      pairSymbol,
      pairIcon: normalizedPairIcon,
      pairIsOtc,
    });
  }, [
    accountLabel,
    currentTimezone,
    losses,
    normalizedPairIcon,
    obsMode,
    pairIsOtc,
    pairSymbol,
    pnl,
    total,
    winRate,
    wins,
  ]);

  useEffect(() => {
    if (!obsMode) return;

    setSize({
      width: Math.max(effectiveMinWidth, sessionCounterStore.windowWidth),
      height: Math.max(minHeight, sessionCounterStore.windowHeight),
    });
  }, [effectiveMinWidth, minHeight, obsMode, sessionCounterStore.windowHeight, sessionCounterStore.windowWidth]);

  const startSession = () => {
    setBets([]);
    pausedBetIdsRef.current.clear();
    sessionCounterStore.resetManualDeltas();
    setPausedElapsedSeconds(null);
    setPausedStartedAtMs(null);
    setTotalPausedMs(0);
    sessionCounterStore.start();
  };

  const resetSession = () => {
    setSettingsOpen(false);
    setTransparencySettingsOpen(false);
    startSession();
  };

  const togglePauseSession = () => {
    if (!startedAt) {
      startSession();
      return;
    }

    if (pausedElapsedSeconds === null) {
      setPausedElapsedSeconds(elapsedSeconds);
      setPausedStartedAtMs(Date.now());
      return;
    }

    if (pausedStartedAtMs !== null) {
      setTotalPausedMs((value) => value + Date.now() - pausedStartedAtMs);
    }
    setPausedElapsedSeconds(null);
    setPausedStartedAtMs(null);
  };

  const minimizeSession = () => {
    setSettingsOpen(false);
    setTransparencySettingsOpen(false);
    setContextMenu(null);
    sessionCounterStore.setMinimized(true);
  };

  const currentLocale =
    typeof window === "undefined" ? "en" : window.location.pathname.match(/^(?:\/app)?\/([^/]+)/)?.[1] ?? "en";
  const obsUrl = (() => {
    if (typeof window === "undefined") return "";

    const params = new URLSearchParams();

    const shareState = sessionCounterStore.shareState;
    if (sessionCounterStore.obsStreamId) params.set("startedAt", sessionCounterStore.obsStreamId);
    params.set("mode", shareState.mode);
    params.set("viewMode", shareState.viewMode);
    params.set("minimized", String(shareState.minimized));
    params.set("metricMode", shareState.metricMode);
    params.set("timezone", shareState.timezone || currentTimezone);
    params.set("footerExpanded", String(shareState.footerExpanded));
    params.set("manualWinsDelta", String(shareState.manualWinsDelta));
    params.set("manualLossesDelta", String(shareState.manualLossesDelta));
    params.set("transparentBackground", String(shareState.transparentBackground));
    params.set("transparency", String(shareState.transparency));
    params.set("windowWidth", String(shareState.windowWidth));
    params.set("windowHeight", String(shareState.windowHeight));

    const appBasePath = import.meta.env.BASE_URL.replace(/\/$/, "");
    const url = new URL(`${appBasePath}/${currentLocale}/session-counter-widget`, window.location.origin);
    url.search = params.toString();
    return url.toString();
  })();

  const copyToClipboard = async (value: string) => {
    sessionCounterStore.publishSyncStateNow();
    await navigator.clipboard.writeText(value);
    setContextMenu(null);
  };

  const handleContextMenu = (event: ReactMouseEvent<HTMLElement>) => {
    if (obsMode) return;

    event.preventDefault();
    const menuWidth = Math.min(
      CONTEXT_MENU_MAX_WIDTH,
      Math.max(CONTEXT_MENU_MIN_WIDTH, estimateSegmentButtonWidth(t("session-counter.copy-link"))),
    );
    const x = Math.min(event.clientX + CONTEXT_MENU_OFFSET, window.innerWidth - menuWidth - CONTEXT_MENU_OFFSET);
    const y = Math.min(event.clientY + CONTEXT_MENU_OFFSET, window.innerHeight - CONTEXT_MENU_MIN_HEIGHT - CONTEXT_MENU_OFFSET);
    setContextMenu({ x: Math.max(CONTEXT_MENU_OFFSET, x), y: Math.max(CONTEXT_MENU_OFFSET, y) });
  };

  useEffect(() => {
    if (!contextMenu) return;

    const closeContextMenu = () => setContextMenu(null);
    window.addEventListener("click", closeContextMenu);
    window.addEventListener("keydown", closeContextMenu);
    return () => {
      window.removeEventListener("click", closeContextMenu);
      window.removeEventListener("keydown", closeContextMenu);
    };
  }, [contextMenu]);

  const contextMenuElement =
    contextMenu && typeof document !== "undefined"
      ? createPortal(
          <div
            className="fixed z-[1000] inline-flex min-w-[140px] max-w-[320px] flex-col gap-1 rounded-[10px] border border-[rgba(140,140,140,0.15)] bg-[#27272A] p-1 text-sm text-[#FBFBFB] shadow-[0_12px_32px_rgba(0,0,0,0.35)]"
            style={{ left: contextMenu.x, top: contextMenu.y }}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="flex min-h-8 items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-[rgba(140,140,140,0.15)]"
              onClick={() => void copyToClipboard(obsUrl)}
            >
              <Icon icon="solar:link-bold" width={16} className="shrink-0 text-[#8C8C8C]" />
              <span className="whitespace-normal break-words">{t("session-counter.copy-link")}</span>
            </button>
          </div>,
          document.body,
        )
      : null;

  if (minimized && !obsMode) return null;

  if (minimized) {
    return (
      <>
        <button
          type="button"
          className={cn(
            "box-border flex h-7 min-w-[58px] items-center gap-2 rounded-lg bg-[rgba(140,140,140,0.15)] px-2 py-1 text-[#FBFBFB] backdrop-blur-[20px]",
            obsMode ? "pointer-events-none relative z-0" : "pointer-events-auto absolute left-4 top-[64px] z-[120]",
            className,
          )}
          onClick={() => sessionCounterStore.setMinimized(false)}
          onContextMenu={handleContextMenu}
        >
          <span className="box-border h-1.5 w-1.5 shrink-0 rounded-full border-2 border-[rgba(23,201,100,0.15)] bg-[#17C964]" />
          <span className="flex h-3.5 shrink-0 items-center gap-1 whitespace-nowrap text-xs font-medium leading-[14px]">
            <span className="flex h-3.5 items-center text-center text-[#17C964]">{displayWins}</span>
            <span className="flex h-3.5 items-center text-center text-[#FBFBFB]">/</span>
            <span className="flex h-3.5 items-center text-center text-[#F31260]">{displayLosses}</span>
          </span>
        </button>
        {contextMenuElement}
      </>
    );
  }

  return (
    <>
      <section
        className={cn(
          "box-border flex flex-col items-start rounded-[14px] border border-[rgba(140,140,140,0.15)] p-2.5 text-[#FBFBFB] shadow-[inset_0_1px_0_1px_rgba(255,255,255,0.024)]",
          isRowView ? "gap-1" : "gap-2",
          isRowView || settingsOpen ? "overflow-visible" : "overflow-hidden",
          obsMode ? "pointer-events-none relative z-0" : "pointer-events-auto absolute z-[120]",
          showBackground && surfaceBackground,
          className,
        )}
        style={{
          left: obsMode ? undefined : position.x,
          top: obsMode ? undefined : position.y,
          width: size.width,
          height: size.height,
          minWidth: effectiveMinWidth,
          minHeight,
          background: showBackground ? undefined : transparentSurfaceBackground,
        }}
        onContextMenu={handleContextMenu}
      >
      <div
        className={cn(
          "flex w-full cursor-grab touch-none items-center active:cursor-grabbing",
          isRowView
            ? "h-[50px] justify-between gap-2 rounded-[14px] border border-[rgba(140,140,140,0.15)] bg-[radial-gradient(100.98%_115.1%_at_50%_0%,rgba(255,184,0,0.055)_0%,rgba(255,184,0,0)_42%),#151515] p-2.5 shadow-[inset_0_1px_0_1px_rgba(255,255,255,0.024)]"
            : "h-11 min-h-[38px] justify-between gap-4 px-[3px] pb-[13.5px] pt-[5.5px]",
        )}
        onPointerDown={startDrag}
      >
        {isRowView ? (
          <>
            <div className="flex h-[17px] w-[77px] shrink-0 items-center gap-[3.99px]">
              <Icon icon="solar:clock-circle-outline" width={12} className="shrink-0 text-[#F4A425]" />
              <span className="flex h-[17px] w-[61px] items-center text-sm font-bold leading-[17px] text-[#FBFBFB]">
                {currentTime}
              </span>
            </div>
            <div className="h-7 w-px shrink-0 bg-[rgba(140,140,140,0.15)]" />
            <div className="flex h-2.5 shrink-0 items-center gap-1">
              <span className="flex h-2.5 items-center text-sm font-semibold leading-[17px] text-[#17C964]">
                {displayWins}
              </span>
              <span className="flex h-2.5 items-center text-sm font-normal leading-[17px] text-[#8C8C8C]">/</span>
              <span className="flex h-2.5 items-center text-sm font-semibold leading-[17px] text-[#F31260]">
                {displayLosses}
              </span>
            </div>
            <div className="h-7 w-px shrink-0 bg-[rgba(140,140,140,0.15)]" />
            <span className="flex h-[9px] w-6 shrink-0 items-center text-xs font-normal leading-[15px] text-[#8C8C8C]">
              {displayWinRate}%
            </span>
            <div className="h-7 w-px shrink-0 bg-[rgba(140,140,140,0.15)]" />
            <div className="flex h-6 w-[72px] shrink-0 items-center">
              <button
                type="button"
                className="flex h-6 w-6 items-center justify-center rounded-md text-[#8C8C8C] transition-colors hover:text-[#F4A425]"
                onClick={resetSession}
              >
                <ResetSessionIcon className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                className={cn(
                  "flex h-6 w-6 items-center justify-center rounded-md text-[#8C8C8C] transition-colors hover:text-[#F4A425]",
                  settingsOpen && "text-[#F4A425]",
                )}
                onClick={() => {
                  setSettingsOpen((value) => {
                    const nextValue = !value;
                    if (!nextValue) setTransparencySettingsOpen(false);
                    return nextValue;
                  });
                }}
              >
                <SettingsGearIcon className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                className="flex h-6 w-6 items-center justify-center rounded-md text-[#8C8C8C] transition-colors hover:text-[#FBFBFB]"
                onClick={minimizeSession}
              >
                <Icon icon="solar:minus-linear" width={14} />
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex min-w-0 items-center gap-2">
              {displayPairIcon ? (
                <img src={displayPairIcon} alt={displayPairSymbol} className="h-6 w-6 shrink-0 object-contain" />
              ) : (
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#2A2A2A] text-[9px] font-bold">
                  {displayPairSymbol.slice(0, 2)}
                </div>
              )}
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="truncate text-xs font-semibold leading-5">{displayPairSymbol}</span>
                {displayPairIsOtc && (
                  <span className="rounded bg-[rgba(255,184,0,0.15)] px-1.5 py-0.5 text-xs font-semibold leading-4 text-[#FFA51F]">
                    OTC
                  </span>
                )}
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-3">
              <div className="flex items-center gap-1.5">
                <Icon icon="solar:clock-circle-outline" width={16} className="text-[#FFA51F]" />
                <span className="text-sm font-bold leading-[17px]">{currentTime}</span>
              </div>
              <button
                type="button"
                className="text-[#8C8C8C] transition-colors hover:text-[#FFA51F]"
                onClick={resetSession}
              >
                <ResetSessionIcon className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                className={cn(
                  "text-[#8C8C8C] transition-colors hover:text-[#FFA51F]",
                  settingsOpen && "text-[#FFA51F]",
                )}
                onClick={() => {
                  setSettingsOpen((value) => {
                    const nextValue = !value;
                    if (!nextValue) setTransparencySettingsOpen(false);
                    return nextValue;
                  });
                }}
              >
                <SettingsGearIcon className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                className="text-[#8C8C8C] transition-colors hover:text-white"
                onClick={minimizeSession}
              >
                <Icon icon="solar:minus-linear" width={18} />
              </button>
            </div>
          </>
        )}
      </div>

      {settingsOpen && (
        <div className="absolute right-[38px] top-[57px] z-10 flex w-[237px] flex-col gap-2 rounded-[14px] border border-[#3A3A3C] bg-[#27272A] p-1 shadow-[0_12px_32px_rgba(0,0,0,0.35)]">
          {[
            ["detailed", t("session-counter.view-detailed"), "solar:hamburger-menu-bold"],
            ["row", t("session-counter.view-row"), "solar:minimize-square-3-linear"],
            ["transparent", t("session-counter.transparency"), "solar:gallery-minimalistic-linear"],
          ].map(([value, label, icon]) => (
            <button
              key={value}
              type="button"
              className={cn(
                "flex h-8 items-center gap-2 rounded-lg px-2 text-left text-sm font-semibold",
                (value === "transparent" ? transparencySettingsOpen : viewMode === value)
                  ? "bg-[#49391F] text-[#FFA51F]"
                  : "text-[#FBFBFB]",
              )}
              onClick={() => {
                if (value === "transparent") {
                  setTransparencySettingsOpen((expanded) => {
                    const nextExpanded = !expanded;
                    if (nextExpanded) sessionCounterStore.setTransparentBackground(true);
                    return nextExpanded;
                  });
                  return;
                }

                sessionCounterStore.setViewMode(value as SessionCounterViewMode);
                if (value === "row") sessionCounterStore.setFooterExpanded(false);
                setTransparencySettingsOpen(false);
                setSettingsOpen(false);
              }}
            >
              <Icon icon={icon ?? "solar:hamburger-menu-bold"} width={16} />
              <span className="truncate">{label}</span>
            </button>
          ))}
          {transparencySettingsOpen && (
            <div className="box-border flex h-[54px] w-full flex-col gap-[11px] rounded-[9px] border border-[#3A3A3C] bg-[#343437] px-3 py-[6px] backdrop-blur-[5px]">
              <div className="flex h-[15px] w-full items-center justify-between">
                <span className="text-xs font-medium leading-[15px] text-[#FBFBFB]">
                  {t("session-counter.transparency")}
                </span>
                <span className="text-xs font-semibold leading-[15px] text-[#F4A425]">
                  {sessionCounterStore.transparency}%
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={99}
                step={1}
                value={sessionCounterStore.transparency}
                onChange={(event) => sessionCounterStore.setTransparency(Number(event.currentTarget.value))}
                className="h-1 w-full cursor-pointer appearance-none rounded-full [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-[#F4A425] [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#F4A425]"
                style={{
                  background: `linear-gradient(to right, #F4A425 ${sessionCounterStore.transparency}%, rgba(140, 140, 140, 0.15) ${sessionCounterStore.transparency}%)`,
                }}
              />
            </div>
          )}
        </div>
      )}

      {isRowView ? (
        <>
          <button
            type="button"
            className="flex h-[16.5px] w-full items-center justify-center rounded-t-lg py-2 text-[#8C8C8C]"
            onClick={() => sessionCounterStore.setFooterExpanded(!footerExpanded)}
          >
            <Icon
              icon={footerExpanded ? "solar:alt-arrow-up-line-duotone" : "solar:alt-arrow-down-line-duotone"}
              width={16}
            />
          </button>

          {footerExpanded && (
            <div className="box-border flex h-auto w-full flex-col gap-2 rounded-[14px] border border-[rgba(140,140,140,0.15)] bg-[radial-gradient(100.98%_115.1%_at_50%_0%,rgba(255,184,0,0.055)_0%,rgba(255,184,0,0)_42%),#151515] p-2.5 shadow-[inset_0_1px_0_1px_rgba(255,255,255,0.024)]">
              {mode === "manual" && (
                <div className="flex h-[89px] w-full items-start gap-2">
                  <div className="box-border flex h-[89px] flex-1 items-start justify-center rounded-[10px] border border-[rgba(140,140,140,0.15)] bg-[rgba(140,140,140,0.15)] py-2">
                    <div className="flex h-[71px] flex-col items-center justify-center gap-2">
                      <div className="flex flex-col items-center gap-[3px]">
                        <span className="flex h-[21px] items-center text-[21px] font-bold leading-[21px] text-[#17C964]">
                          {displayWins}
                        </span>
                        <span className="flex h-[15px] items-center text-xs font-normal leading-[14px] text-[#8C8C8C]">
                          {t("session-counter.profitable")}
                        </span>
                      </div>
                      <CounterButtons
                        onIncrement={() => sessionCounterStore.incrementManualWinsDelta()}
                        onDecrement={() => sessionCounterStore.decrementManualWinsDelta()}
                        decrementDisabled={displayWins <= 0}
                      />
                    </div>
                  </div>

                  <div className="box-border flex h-[89px] flex-1 items-start justify-center rounded-[10px] border border-[rgba(140,140,140,0.15)] bg-[rgba(140,140,140,0.15)] py-2">
                    <div className="flex h-[71px] flex-col items-center justify-center gap-2">
                      <div className="flex flex-col items-center gap-[3px]">
                        <span className="flex h-[21px] items-center text-[21px] font-bold leading-[21px] text-[#F31260]">
                          {displayLosses}
                        </span>
                        <span className="flex h-[15px] items-center text-xs font-normal leading-[14px] text-[#8C8C8C]">
                          {t("session-counter.unprofitable")}
                        </span>
                      </div>
                      <CounterButtons
                        onIncrement={() => sessionCounterStore.incrementManualLossesDelta()}
                        onDecrement={() => sessionCounterStore.decrementManualLossesDelta()}
                        decrementDisabled={displayLosses <= 0}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="flex h-9 w-full items-center gap-[7px]">
                <div className="grid h-9 flex-1 grid-cols-2 rounded-[9px] border border-[#3A3A3C] bg-[#27272A] p-[3px]">
                  <button
                    type="button"
                    className={cn(
                      "flex h-[27px] items-center justify-center gap-1 rounded-md px-1 text-xs font-bold leading-[15px]",
                      mode === "auto"
                        ? "border border-[#5A411D] bg-[#49391F] text-[#F4A425]"
                        : "text-[#8C8C8C]",
                    )}
                    onClick={() => sessionCounterStore.setMode("auto")}
                  >
                    <Icon icon="solar:bolt-bold" width={14} className="shrink-0" />
                    <span className="whitespace-nowrap">{t("session-counter.auto-count")}</span>
                  </button>
                  <button
                    type="button"
                    className={cn(
                      "flex h-[27px] items-center justify-center gap-1 rounded-md px-1 text-xs font-bold leading-[15px]",
                      mode === "manual"
                        ? "border border-[#5A411D] bg-[#49391F] text-[#F4A425]"
                        : "text-[#8C8C8C]",
                    )}
                    onClick={() => sessionCounterStore.setMode("manual")}
                  >
                    <Icon icon="solar:pen-bold" width={14} className="shrink-0" />
                    <span className="whitespace-nowrap">{t("session-counter.manual")}</span>
                  </button>
                </div>
                <button
                  type="button"
                  className="flex h-9 w-[38px] shrink-0 items-center justify-center rounded-[9px] border border-[#3A3A3C] bg-[#27272A] text-[#F4A425]"
                  onClick={togglePauseSession}
                >
                  <Icon
                    icon={
                      isStarted
                        ? pausedElapsedSeconds === null
                          ? "solar:pause-circle-bold"
                          : "solar:play-circle-bold"
                        : "solar:play-circle-bold"
                    }
                    width={20}
                  />
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {mode === "manual" ? (
            <div className="flex h-20 w-full items-start justify-center rounded-[10px] border border-[rgba(140,140,140,0.15)] bg-[rgba(140,140,140,0.15)]">
              <ManualStatValue
                value={displayWins}
                label={t("session-counter.profitable")}
                tone="green"
                onIncrement={() => sessionCounterStore.incrementManualWinsDelta()}
                onDecrement={() => sessionCounterStore.decrementManualWinsDelta()}
                decrementDisabled={displayWins <= 0}
              />
              <div className="h-14 w-px self-center bg-[rgba(140,140,140,0.15)]" />
              <StatValue value={displayTotal} label={t("session-counter.deals")} />
              <div className="h-14 w-px self-center bg-[rgba(140,140,140,0.15)]" />
              <ManualStatValue
                value={displayLosses}
                label={t("session-counter.unprofitable")}
                tone="pink"
                onIncrement={() => sessionCounterStore.incrementManualLossesDelta()}
                onDecrement={() => sessionCounterStore.decrementManualLossesDelta()}
                decrementDisabled={displayLosses <= 0}
              />
            </div>
          ) : (
            <div className="flex h-[61px] w-full items-start justify-center rounded-[10px] border border-[rgba(140,140,140,0.15)] bg-[rgba(140,140,140,0.15)]">
              <StatValue value={displayWins} label={t("session-counter.profitable")} tone="green" />
              <div className="h-[34px] w-px bg-[rgba(140,140,140,0.15)]" />
              <StatValue value={displayTotal} label={t("session-counter.deals")} />
              <div className="h-[34px] w-px bg-[rgba(140,140,140,0.15)]" />
              <StatValue value={displayLosses} label={t("session-counter.unprofitable")} tone="pink" />
            </div>
          )}

          <div className="flex h-[53px] min-h-[52px] w-full items-center rounded-[10px] border border-[rgba(140,140,140,0.15)] bg-[rgba(140,140,140,0.15)] px-3 py-[7.5px]">
            <div className="flex h-[35px] flex-1 flex-col justify-center gap-[3px]">
              <span className="text-xs leading-[15px] text-[#8C8C8C]">{t("session-counter.session")}</span>
              <span className="text-[17px] font-bold leading-[17px] text-[#FBFBFB]">
                {isStarted ? formatElapsed(displayElapsedSeconds) : "-"}
              </span>
            </div>
            <div className="h-7 w-px bg-[rgba(140,140,140,0.15)]" />
            <div className="flex h-9 flex-1 flex-col items-end justify-center gap-[3px]">
              <span className="text-xs leading-[15px] text-[#8C8C8C]">
                {metricMode === "profit" ? t("session-counter.profit") : t("session-counter.win-rate")}
              </span>
              <span className={cn("text-lg font-bold leading-[18px]", metricValueClassName)}>
                {isStarted ? (metricMode === "profit" ? formatMoney(displayPnl) : `${displayWinRate}%`) : "-"}
              </span>
            </div>
          </div>

          <div className="flex w-full flex-col gap-[7px]">
            {isStarted && (
              <div className="grid h-9 w-full grid-cols-2 rounded-[9px] border border-[rgba(140,140,140,0.15)] bg-[rgba(140,140,140,0.15)] p-[3px]">
                <button
                  type="button"
                  className={cn(
                    "flex h-[27px] items-center justify-center gap-1 rounded-md px-1 text-xs font-bold leading-[15px]",
                    metricMode === "profit"
                      ? "border border-[rgba(244,164,37,0.15)] bg-[rgba(244,164,37,0.15)] text-[#F4A425]"
                      : "text-[#8C8C8C]",
                  )}
                  onClick={() => sessionCounterStore.setMetricMode("profit")}
                >
                  <ProfitIcon className="h-3.5 w-3.5 shrink-0" />
                  <span className="whitespace-nowrap">{t("session-counter.profit")}</span>
                </button>
                <button
                  type="button"
                  className={cn(
                    "flex h-[27px] items-center justify-center gap-1 rounded-md px-1 text-xs font-bold leading-[15px]",
                    metricMode === "winrate"
                      ? "border border-[rgba(244,164,37,0.15)] bg-[rgba(244,164,37,0.15)] text-[#F4A425]"
                      : "text-[#8C8C8C]",
                  )}
                  onClick={() => sessionCounterStore.setMetricMode("winrate")}
                >
                  <WinRateIcon className="h-3.5 w-3.5 shrink-0" />
                  <span className="whitespace-nowrap">{t("session-counter.win-rate")}</span>
                </button>
              </div>
            )}

            <button
              type="button"
              className="flex h-[16.5px] w-full items-center justify-center rounded-t-lg text-[#8C8C8C]"
              onClick={() => sessionCounterStore.setFooterExpanded(!footerExpanded)}
            >
              <Icon
                icon={footerExpanded ? "solar:alt-arrow-up-line-duotone" : "solar:alt-arrow-down-line-duotone"}
                width={16}
              />
            </button>

            {footerExpanded && (
              <div className="flex h-9 w-full items-center gap-[7px]">
                <div className="grid h-9 flex-1 grid-cols-2 rounded-[9px] border border-[#3A3A3C] bg-[#27272A] p-[3px]">
                  <button
                    type="button"
                    className={cn(
                      "flex h-[27px] items-center justify-center gap-1 rounded-md px-1 text-xs font-bold leading-[15px]",
                      mode === "auto"
                        ? "border border-[#5A411D] bg-[#49391F] text-[#F4A425]"
                        : "text-[#8C8C8C]",
                    )}
                    onClick={() => sessionCounterStore.setMode("auto")}
                  >
                    <Icon icon="solar:bolt-bold" width={14} className="shrink-0" />
                    <span className="whitespace-nowrap">{t("session-counter.auto-count")}</span>
                  </button>
                  <button
                    type="button"
                    className={cn(
                      "flex h-[27px] items-center justify-center gap-1 rounded-md px-1 text-xs font-bold leading-[15px]",
                      mode === "manual"
                        ? "border border-[#5A411D] bg-[#49391F] text-[#F4A425]"
                        : "text-[#8C8C8C]",
                    )}
                    onClick={() => sessionCounterStore.setMode("manual")}
                  >
                    <Icon icon="solar:pen-bold" width={14} className="shrink-0" />
                    <span className="whitespace-nowrap">{t("session-counter.manual")}</span>
                  </button>
                </div>
                <button
                  type="button"
                  className="flex h-9 w-[38px] shrink-0 items-center justify-center rounded-[9px] border border-[#3A3A3C] bg-[#27272A] text-[#F4A425]"
                  onClick={togglePauseSession}
                >
                  <Icon
                    icon={
                      isStarted
                        ? pausedElapsedSeconds === null
                          ? "solar:pause-circle-bold"
                          : "solar:play-circle-bold"
                        : "solar:play-circle-bold"
                    }
                    width={20}
                  />
                </button>
              </div>
            )}
          </div>
        </>
      )}
      </section>
      {contextMenuElement}
    </>
  );
});
